import { readFileSync, existsSync, readdirSync } from "node:fs";
import { resolve, join } from "node:path";
import type { DriftIssue } from "../../types.js";

/** Scan .env.example / .env.template for declared variable names */
function loadEnvExampleVars(projectRoot: string): Set<string> | null {
  const candidates = [".env.example", ".env.template", ".env.sample"];
  for (const name of candidates) {
    const path = resolve(projectRoot, name);
    if (!existsSync(path)) continue;
    try {
      const vars = new Set<string>();
      for (const line of readFileSync(path, "utf-8").split("\n")) {
        const trimmed = line.trim();
        if (!trimmed || trimmed.startsWith("#")) continue;
        const match = trimmed.match(/^([A-Z_][A-Z0-9_]*)\s*=/i);
        if (match) vars.add(match[1].toUpperCase());
      }
      return vars.size > 0 ? vars : null;
    } catch {
      continue;
    }
  }
  return null;
}

/** Recursively collect process.env.VAR usages from TS/JS source files */
function collectFromDir(dir: string, out: Set<string>): void {
  let entries: import("node:fs").Dirent[];
  try {
    entries = readdirSync(dir, { withFileTypes: true, encoding: "utf-8" }) as import("node:fs").Dirent[];
  } catch {
    return;
  }
  for (const entry of entries) {
    const full = join(dir, entry.name as string);
    if (entry.isDirectory()) {
      collectFromDir(full, out);
    } else if (entry.isFile() && /\.(ts|tsx|js|jsx|mjs|cjs)$/.test(entry.name)) {
      try {
        collectEnvUsagesFromContent(readFileSync(full, "utf-8"), out);
      } catch {
        // skip unreadable files
      }
    }
  }
}

/**
 * Collect process.env.VAR usages from source text, skipping comment lines so a
 * documentation reference like `process.env.VAR` in a JSDoc block is not
 * mistaken for a real env var. Exported for testing.
 */
export function collectEnvUsagesFromContent(content: string, out: Set<string>): void {
  const re = /process\.env\.([A-Z_][A-Z0-9_]*)/gi;
  for (const rawLine of content.split("\n")) {
    const line = rawLine.trim();
    // Skip single-line and block-comment lines (// ... and * ... / /* ...).
    if (line.startsWith("//") || line.startsWith("*") || line.startsWith("/*")) continue;
    let m: RegExpExecArray | null;
    re.lastIndex = 0;
    while ((m = re.exec(rawLine)) !== null) {
      out.add(m[1].toUpperCase());
    }
  }
}

function collectEnvUsagesFromSrc(projectRoot: string): Set<string> {
  const vars = new Set<string>();
  for (const dir of ["src", "lib", "app"]) {
    const full = resolve(projectRoot, dir);
    if (existsSync(full)) collectFromDir(full, vars);
  }
  return vars;
}

/** Check that env vars declared in .env.example and used in src/ are mentioned in scaffold */
export function checkEnvVars(
  scaffoldFiles: string[],
  projectRoot: string,
  scaffoldText: string
): DriftIssue[] {
  const fromExample = loadEnvExampleVars(projectRoot);
  const fromSrc = collectEnvUsagesFromSrc(projectRoot);

  const allVars = new Set<string>([...(fromExample ?? []), ...fromSrc]);
  if (allVars.size === 0) return [];

  const issues: DriftIssue[] = [];

  for (const varName of allVars) {
    if (scaffoldText.includes(varName)) continue;

    const inExample = fromExample?.has(varName) ?? false;
    const inSrc = fromSrc.has(varName);
    const origin =
      inExample && inSrc
        ? ".env.example and source code"
        : inExample
        ? ".env.example"
        : "source code";

    issues.push({
      code: "UNDOCUMENTED_ENV_VAR",
      severity: "warning",
      file: inExample ? ".env.example" : "src/",
      line: null,
      message: `Env var "${varName}" found in ${origin} but not mentioned in any scaffold file`,
      explanation: `${varName} is used by the application but not documented in the scaffold. Agents won't know it exists or what it's for.`,
      suggestion: `Add "${varName}" to the Environment Variables section in context/setup.md or equivalent scaffold file.`,
    });
  }

  return issues;
}
